import styled from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';

/** Matches home page tab styling; horizontal scroll when tabs overflow. */
export const UnderlineTabRow = styled.div`
  display: flex;
  gap: 0;
  flex-wrap: nowrap;
  align-items: flex-end;
  margin-top: 0;
  border-bottom: 1px solid ${Constants.HairlineBorderRgba};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const UnderlineTab = styled.button.attrs({ type: 'button' })`
  padding: ${(p) =>
    p.$alignStart ? '0.85rem 1.35rem 0.75rem 0' : '0.85rem 1.35rem 0.75rem'};
  margin: 0;
  margin-bottom: -1px;
  border: none;
  background: none;
  appearance: none;
  -webkit-appearance: none;
  font-weight: 600;
  font-size: ${Constants.TabLabelFontSize};
  letter-spacing: -0.01em;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: ${(p) => (p.$alignStart ? 'left' : 'center')};
  font-family: inherit;
  color: ${(p) => (p.$active ? Constants.DarkTextColor : Constants.MediumTextGray)};
  border-bottom: 2px solid ${(p) => (p.$active ? Constants.DarkTextColor : 'transparent')};
  transition: color 0.15s ease, border-color 0.15s ease;
  &:hover {
    color: ${Constants.DarkTextColor};
  }
`;

export const UnderlineTabDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: ${Constants.HairlineBorderRgba};
  margin: 0.65rem 0.35rem 0.85rem;
  flex-shrink: 0;
`;

/** Pill / segmented control — optional use via TabBar variant segment. */
export const SegmentTabTrack = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  padding: 5px;
  gap: 3px;
  background: rgba(0, 0, 0, 0.055);
  border-radius: 999px;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  box-sizing: border-box;
`;

export const SegmentTab = styled.button.attrs({ type: 'button' })`
  margin: 0;
  border: none;
  appearance: none;
  -webkit-appearance: none;
  font-family: inherit;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  padding: ${(p) => (p.$compact ? '0.4rem 0.85rem' : '0.5rem 1.2rem')};
  border-radius: 999px;
  font-weight: 600;
  font-size: ${(p) => (p.$compact ? '0.8125rem' : '0.875rem')};
  letter-spacing: -0.01em;
  color: ${(p) => (p.$active ? Constants.DarkTextColor : Constants.MediumTextGray)};
  background: ${(p) => (p.$active ? Constants.White : 'transparent')};
  box-shadow: ${(p) =>
    p.$active ? '0 1px 4px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.04)' : 'none'};
  transition: color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    color: ${Constants.DarkTextColor};
  }
`;

/** Home hero + search — primary-tinted pills under a hairline. */
export const HomeTabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1.05rem;
  padding-bottom: 0.65rem;
  border-bottom: ${Constants.BottomBorderDivider};
`;

export const HomeTabButton = styled.button.attrs({ type: 'button' })`
  ${(p) => p.$stretch && 'flex: 1; min-width: 0;'}
  border: none;
  background: ${(p) =>
    p.$active ? `color-mix(in srgb, ${Constants.PrimaryColor} 12%, transparent)` : 'transparent'};
  color: ${(p) => (p.$active ? Constants.PrimaryColor : Constants.MediumTextGray)};
  font-weight: ${(p) => (p.$active ? 700 : 600)};
  font-size: ${Constants.TabLabelFontSize};
  padding: ${(p) => (p.$compact ? '0.45rem 0.75rem' : '0.45rem 0.95rem')};
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease, background 0.15s ease;
  &:hover {
    color: ${Constants.PrimaryColor};
    background: ${(p) =>
      p.$active
        ? `color-mix(in srgb, ${Constants.PrimaryColor} 16%, transparent)`
        : 'rgba(0, 0, 0, 0.04)'};
  }
`;

/** Scroll shell for pill variant (ex-FilterBar). */
export const PillBarShell = styled.div`
  position: relative;
  overflow: hidden;
  height: 50px;
  > div:last-child {
    transition: 0.5s all;
    opacity: 0;
    ${Constants.MediaMobileAndTablet} {
      display: none;
    }
  }
  :hover {
    > div:last-child {
      ${Constants.MediaDesktop} {
        opacity: 1;
      }
    }
  }
`;

export const PillTab = styled.button.attrs({ type: 'button' })`
  cursor: pointer;
  min-width: 4rem;
  text-align: center;
  border-radius: 1.5rem;
  font-size: 0.75rem;
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  user-select: none;
  border: none;
  font-family: inherit;
  background-color: ${(p) => (p.$active ? Constants.DarkTextColor : Constants.MediumBackground)};
  color: ${(p) => (p.$active ? 'white' : 'unset')};
`;

export const PillBarArrows = styled.div`
  > div {
    position: absolute;
    height: 50px;
    width: 60px;
    top: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    > div {
      border-radius: 50%;
      height: 1.5rem;
      width: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  > div:first-child {
    left: 0;
    background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 1) 100%);
    justify-content: flex-start;
  }
  > div:last-child {
    right: 0;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 1) 100%);
    justify-content: flex-end;
  }
`;
